import React, { useState, useMemo } from 'react';
import { Search, BookOpen, Star, Moon, Sun, Sparkles, ArrowUp } from 'lucide-react';
import AuthorBio from '../components/AuthorBio';
import AdSpace from '../components/AdSpace';
import { useSetMeta } from '../hooks/useSetMeta';

interface GlossaryTerm {
    term: string;
    definition: string;
    category: 'western' | 'chinese' | 'tarot' | 'general' | 'elements';
    relatedTerms?: string[];
}

const GLOSSARY_TERMS: GlossaryTerm[] = [
    // Western Astrology Terms
    {
        term: "Ascendant (Rising Sign)",
        definition: "The zodiac sign that was rising on the eastern horizon at the exact moment of your birth. The Ascendant represents your outward personality, first impressions, and how others perceive you. It changes approximately every two hours, making birth time essential for accurate calculation. Together with the Sun and Moon signs, it forms your 'Big Three' in astrology.",
        category: "western",
        relatedTerms: ["Sun Sign", "Moon Sign", "Birth Chart"]
    },
    {
        term: "Aspects",
        definition: "The angular relationships between planets in a birth chart, measured in degrees. Major aspects include conjunction (0°), sextile (60°), square (90°), trine (120°), and opposition (180°). Aspects describe how planetary energies interact—harmoniously (trines, sextiles) or with tension (squares, oppositions). They add nuance and complexity to astrological interpretation.",
        category: "western",
        relatedTerms: ["Birth Chart", "Planets", "Houses"]
    },
    {
        term: "Birth Chart (Natal Chart)",
        definition: "A map of the sky at the exact moment and location of your birth, showing the positions of the Sun, Moon, planets, and other celestial points relative to the zodiac signs and houses. The birth chart is the foundation of personal astrology, revealing personality traits, life themes, challenges, and potentials unique to you.",
        category: "western",
        relatedTerms: ["Ascendant", "Houses", "Planets"]
    },
    {
        term: "Cardinal Signs",
        definition: "The four zodiac signs that mark the beginning of each season: Aries (spring), Cancer (summer), Libra (autumn), and Capricorn (winter). Cardinal signs are associated with initiation, leadership, and taking action. People with strong cardinal energy often excel at starting new projects and driving change.",
        category: "western",
        relatedTerms: ["Modality", "Fixed Signs", "Mutable Signs"]
    },
    {
        term: "Cusp",
        definition: "The dividing line between two zodiac signs or houses in a birth chart. Being 'born on the cusp' means your birthday falls within a few days of the Sun moving from one sign to another. While popular culture suggests cusp-born individuals share traits of both signs, traditional astrology emphasizes that the Sun is always in one specific sign at the moment of birth.",
        category: "western",
        relatedTerms: ["Sun Sign", "Houses", "Birth Chart"]
    },
    {
        term: "Decan",
        definition: "A subdivision of each zodiac sign into three 10-degree segments, totaling 36 decans in the zodiac. Each decan is said to add subtle variations to a sign's characteristics. For example, the first decan of Aries (0-10°) is most purely Aries, while the second and third decans are influenced by Leo and Sagittarius respectively.",
        category: "western",
        relatedTerms: ["Zodiac Signs", "Degrees", "Elements"]
    },
    {
        term: "Descendant",
        definition: "The point directly opposite the Ascendant in a birth chart, marking the cusp of the 7th house. The Descendant represents partnerships, marriage, and qualities we seek in others. It shows what we project onto partners and the types of relationships we attract.",
        category: "western",
        relatedTerms: ["Ascendant", "Houses", "7th House"]
    },
    {
        term: "Ecliptic",
        definition: "The apparent path the Sun traces across the sky over the course of a year, as observed from Earth. The zodiac signs are divisions of this ecliptic into twelve 30-degree segments. All planets in our solar system orbit roughly along the ecliptic plane, which is why they appear to move through the zodiac signs.",
        category: "western",
        relatedTerms: ["Zodiac", "Sun", "Planets"]
    },
    {
        term: "Fixed Signs",
        definition: "The four zodiac signs that fall in the middle of each season: Taurus (mid-spring), Leo (mid-summer), Scorpio (mid-autumn), and Aquarius (mid-winter). Fixed signs are associated with persistence, determination, and stability. People with strong fixed energy often excel at maintaining projects and seeing things through to completion.",
        category: "western",
        relatedTerms: ["Modality", "Cardinal Signs", "Mutable Signs"]
    },
    {
        term: "Houses",
        definition: "The twelve divisions of a birth chart, each representing a different area of life. The 1st house represents self and identity; the 2nd, finances and values; the 3rd, communication; the 4th, home and family; the 5th, creativity and romance; the 6th, health and work; the 7th, partnerships; the 8th, transformation; the 9th, philosophy and travel; the 10th, career; the 11th, community; and the 12th, spirituality and the unconscious.",
        category: "western",
        relatedTerms: ["Birth Chart", "Ascendant", "Planets"]
    },
    {
        term: "Midheaven (MC)",
        definition: "The highest point in a birth chart, marking the cusp of the 10th house. The Midheaven represents career, public image, reputation, and life goals. It shows how the world sees your achievements and what you aspire to accomplish professionally and socially.",
        category: "western",
        relatedTerms: ["Houses", "Career", "10th House"]
    },
    {
        term: "Modality",
        definition: "A classification of zodiac signs into three groups based on their relationship to the seasons: Cardinal signs (initiators), Fixed signs (stabilizers), and Mutable signs (adapters). Each modality contains one sign from each element, creating a balanced system. Understanding modality reveals how signs approach action, change, and stability.",
        category: "western",
        relatedTerms: ["Cardinal Signs", "Fixed Signs", "Mutable Signs"]
    },
    {
        term: "Moon Sign",
        definition: "The zodiac sign the Moon occupied at the moment of your birth. The Moon sign represents your emotional nature, instincts, habits, and subconscious patterns. It shows how you process feelings, what makes you feel secure, and your emotional needs in relationships. The Moon moves through each sign approximately every 2.5 days.",
        category: "western",
        relatedTerms: ["Sun Sign", "Ascendant", "Big Three"]
    },
    {
        term: "Mutable Signs",
        definition: "The four zodiac signs that fall at the end of each season: Gemini (late spring), Virgo (late summer), Sagittarius (late autumn), and Pisces (late winter). Mutable signs are associated with adaptability, flexibility, and transformation. People with strong mutable energy often excel at navigating change and transitions.",
        category: "western",
        relatedTerms: ["Modality", "Cardinal Signs", "Fixed Signs"]
    },
    {
        term: "Natal",
        definition: "Relating to birth. In astrology, 'natal' typically refers to the positions of celestial bodies at the time of birth. A natal chart is another name for a birth chart. Natal astrology focuses on interpreting these birth positions to understand personality, life themes, and potential experiences.",
        category: "western",
        relatedTerms: ["Birth Chart", "Transit", "Progression"]
    },
    {
        term: "Opposition",
        definition: "An aspect of 180 degrees between two planets, indicating they are on opposite sides of the zodiac. Oppositions represent tension, polarity, and the need for balance between competing energies. While challenging, oppositions can promote growth through awareness of different perspectives.",
        category: "western",
        relatedTerms: ["Aspects", "Conjunction", "Square"]
    },
    {
        term: "Orb",
        definition: "The degree of allowance given when determining whether an aspect is exact. For example, if a conjunction is exact at 0 degrees, an orb of 8 degrees means planets within 8 degrees of exact conjunction are still considered conjunct. Tight orbs indicate stronger, more noticeable aspects.",
        category: "western",
        relatedTerms: ["Aspects", "Degrees", "Birth Chart"]
    },
    {
        term: "Planets",
        definition: "In astrology, the term 'planets' includes the Sun, Moon, Mercury, Venus, Mars, Jupiter, Saturn, Uranus, Neptune, and Pluto (plus asteroids in some systems). Each planet represents different drives, energies, and areas of life. Their positions in signs and houses, plus aspects between them, form the core of chart interpretation.",
        category: "western",
        relatedTerms: ["Birth Chart", "Aspects", "Houses"]
    },
    {
        term: "Retrograde",
        definition: "The apparent backward motion of a planet as observed from Earth. Planets don't actually move backward, but due to orbital mechanics, they appear to from our perspective. Astrologically, retrograde periods are associated with review, reflection, delays, and revisiting themes related to that planet. Mercury retrograde is the most discussed.",
        category: "western",
        relatedTerms: ["Planets", "Mercury Retrograde", "Transit"]
    },
    {
        term: "Ruling Planet",
        definition: "The planet traditionally associated with governing or having special affinity with a zodiac sign. For example, Mars rules Aries, Venus rules Taurus and Libra, Mercury rules Gemini and Virgo, and so on. The ruling planet's condition in a chart has extra significance for natives of that sign.",
        category: "western",
        relatedTerms: ["Zodiac Signs", "Planets", "Dignities"]
    },
    {
        term: "Saturn Return",
        definition: "The astrological event occurring when Saturn returns to the exact position it occupied at your birth, happening approximately every 29.5 years (around ages 28-30, 57-60, and 86-88). Saturn Returns are associated with major life transitions, increased responsibility, maturation, and confronting life structures. Many consider the first Saturn Return a rite of passage into full adulthood.",
        category: "western",
        relatedTerms: ["Saturn", "Transit", "Life Cycles"]
    },
    {
        term: "Sun Sign",
        definition: "The zodiac sign the Sun occupied at the moment of your birth. This is the sign most people know ('I'm a Leo') and forms the basis of horoscope columns. The Sun sign represents your core identity, ego, vitality, and conscious self. It shows fundamental character traits and how you express your individuality.",
        category: "western",
        relatedTerms: ["Moon Sign", "Ascendant", "Big Three"]
    },
    {
        term: "Transit",
        definition: "The current movement of planets through the zodiac as they form aspects to positions in your natal chart. Transits are used in predictive astrology to understand current influences and timing of events. When transiting Saturn squares your natal Moon, for example, you might experience emotional challenges or responsibilities.",
        category: "western",
        relatedTerms: ["Natal Chart", "Planets", "Aspects"]
    },
    {
        term: "Trine",
        definition: "An aspect of 120 degrees between two planets, considered the most harmonious major aspect. Trines represent natural talent, ease, and flow between the energies involved. Signs of the same element (Fire, Earth, Air, Water) are always in trine to each other.",
        category: "western",
        relatedTerms: ["Aspects", "Elements", "Sextile"]
    },
    {
        term: "Zodiac",
        definition: "The band of sky divided into twelve 30-degree segments, each named after a constellation. In Western (tropical) astrology, the zodiac begins at the spring equinox point (0° Aries), regardless of the current position of constellations due to precession. The zodiac provides the backdrop against which planetary movements are interpreted.",
        category: "western",
        relatedTerms: ["Signs", "Ecliptic", "Constellations"]
    },

    // Elements
    {
        term: "Air Signs",
        definition: "Gemini, Libra, and Aquarius—the three zodiac signs associated with the Air element. Air signs are characterized by intellect, communication, social connection, and abstract thinking. They tend to be curious, articulate, and relationship-oriented, approaching life through ideas, concepts, and ideals rather than emotions or practicality.",
        category: "elements",
        relatedTerms: ["Elements", "Gemini", "Libra", "Aquarius"]
    },
    {
        term: "Earth Signs",
        definition: "Taurus, Virgo, and Capricorn—the three zodiac signs associated with the Earth element. Earth signs are characterized by practicality, reliability, sensuality, and material focus. They tend to be grounded, patient, and hardworking, approaching life through tangible results, physical experiences, and steady progress.",
        category: "elements",
        relatedTerms: ["Elements", "Taurus", "Virgo", "Capricorn"]
    },
    {
        term: "Elements (Four Elements)",
        definition: "The classification of zodiac signs into Fire, Earth, Air, and Water. Each element contains three signs sharing fundamental characteristics. Fire signs (Aries, Leo, Sagittarius) are passionate and energetic. Earth signs (Taurus, Virgo, Capricorn) are practical and grounded. Air signs (Gemini, Libra, Aquarius) are intellectual and communicative. Water signs (Cancer, Scorpio, Pisces) are emotional and intuitive.",
        category: "elements",
        relatedTerms: ["Fire Signs", "Earth Signs", "Air Signs", "Water Signs"]
    },
    {
        term: "Fire Signs",
        definition: "Aries, Leo, and Sagittarius—the three zodiac signs associated with the Fire element. Fire signs are characterized by enthusiasm, courage, passion, and dynamic energy. They tend to be confident, optimistic, and action-oriented, approaching life with boldness and a desire to inspire and lead.",
        category: "elements",
        relatedTerms: ["Elements", "Aries", "Leo", "Sagittarius"]
    },
    {
        term: "Water Signs",
        definition: "Cancer, Scorpio, and Pisces—the three zodiac signs associated with the Water element. Water signs are characterized by emotional depth, intuition, sensitivity, and empathy. They tend to be nurturing, perceptive, and imaginative, approaching life through feelings, instincts, and deep psychological awareness.",
        category: "elements",
        relatedTerms: ["Elements", "Cancer", "Scorpio", "Pisces"]
    },

    // Chinese Zodiac Terms
    {
        term: "Chinese Zodiac (Shēngxiào)",
        definition: "A 12-year cycle where each year is associated with an animal sign: Rat, Ox, Tiger, Rabbit, Dragon, Snake, Horse, Goat, Monkey, Rooster, Dog, and Pig. Unlike Western astrology based on birth month, the Chinese zodiac is determined primarily by birth year. Each animal carries distinct personality traits, compatibility patterns, and symbolic meanings.",
        category: "chinese",
        relatedTerms: ["Lunar Calendar", "Five Elements", "Yin and Yang"]
    },
    {
        term: "Five Elements (Wu Xing)",
        definition: "The Chinese philosophical system of Wood, Fire, Earth, Metal, and Water. In Chinese astrology, each year and animal sign is associated with one of these elements, cycling every 60 years (12 animals × 5 elements). The element modifies the base animal characteristics: Wood adds creativity, Fire adds passion, Earth adds stability, Metal adds determination, and Water adds wisdom.",
        category: "chinese",
        relatedTerms: ["Chinese Zodiac", "Element Cycles", "Compatibility"]
    },
    {
        term: "Lunar Calendar",
        definition: "A calendar system based on the cycles of the Moon, used in Chinese tradition to determine New Year and zodiac sign boundaries. The Chinese New Year falls between late January and mid-February. If you were born in January or early February, your Chinese zodiac sign may belong to the previous year—check the exact Lunar New Year date for your birth year.",
        category: "chinese",
        relatedTerms: ["Chinese Zodiac", "Chinese New Year", "Birth Year"]
    },
    {
        term: "Yin and Yang",
        definition: "The fundamental Chinese concept of complementary opposites. In the zodiac, animals alternate between Yin (feminine, receptive, passive) and Yang (masculine, active, assertive). Rat, Tiger, Dragon, Horse, Monkey, and Dog are Yang. Ox, Rabbit, Snake, Goat, Rooster, and Pig are Yin. This polarity influences the animal's expression of traits.",
        category: "chinese",
        relatedTerms: ["Chinese Zodiac", "Balance", "Polarity"]
    },
    {
        term: "Zodiac Trine",
        definition: "The grouping of Chinese zodiac animals into four sets of three based on compatibility. Animals within the same trine share fundamental values and temperaments. The First Trine (Rat, Dragon, Monkey) are action-oriented. The Second Trine (Ox, Snake, Rooster) are determined. The Third Trine (Tiger, Horse, Dog) are freedom-loving. The Fourth Trine (Rabbit, Goat, Pig) are peace-seeking.",
        category: "chinese",
        relatedTerms: ["Chinese Zodiac", "Compatibility", "Animal Signs"]
    },
    {
        term: "Ben Ming Nian",
        definition: "Your 'birth year' or 'fate year'—the year when your Chinese zodiac animal comes around again (every 12 years). For example, if you're a Dragon, 2024 (Year of the Dragon) is your Ben Ming Nian. Traditionally considered a year of challenges and changes, many wear red during this year for protection and luck.",
        category: "chinese",
        relatedTerms: ["Chinese Zodiac", "Lunar Year", "Fortune"]
    },

    // Tarot Terms
    {
        term: "Arcana",
        definition: "Latin for 'secrets' or 'mysteries.' In tarot, the deck is divided into Major Arcana (22 cards representing significant life themes and spiritual lessons) and Minor Arcana (56 cards representing everyday situations and practical matters). Major Arcana readings often indicate profound life events or spiritual development.",
        category: "tarot",
        relatedTerms: ["Major Arcana", "Minor Arcana", "Tarot Deck"]
    },
    {
        term: "Court Cards",
        definition: "The 16 personality cards in the Minor Arcana, consisting of Page, Knight, Queen, and King of each suit. Court cards often represent people in your life, aspects of yourself, or energies you need to embody. Pages suggest messages and beginnings, Knights indicate action, Queens represent mastery, and Kings signify authority.",
        category: "tarot",
        relatedTerms: ["Minor Arcana", "Suits", "Personality"]
    },
    {
        term: "Fool's Journey",
        definition: "The narrative arc of the 22 Major Arcana cards, telling the story of spiritual development from innocence (The Fool, card 0) through various life experiences to completion (The World, card 21). This framework helps understand each Major Arcana card as a stage in personal growth and life lessons.",
        category: "tarot",
        relatedTerms: ["Major Arcana", "The Fool", "The World"]
    },
    {
        term: "Major Arcana",
        definition: "The 22 trump cards of a tarot deck, numbered 0-21, representing significant life themes, archetypal energies, and spiritual lessons. Cards include The Fool, The Magician, The High Priestess, The Empress, The Emperor, The Hierophant, The Lovers, The Chariot, Strength, The Hermit, Wheel of Fortune, Justice, The Hanged Man, Death, Temperance, The Devil, The Tower, The Star, The Moon, The Sun, Judgement, and The World.",
        category: "tarot",
        relatedTerms: ["Arcana", "Minor Arcana", "Fool's Journey"]
    },
    {
        term: "Minor Arcana",
        definition: "The 56 cards of a tarot deck divided into four suits (Wands, Cups, Swords, Pentacles), each containing cards Ace through 10 plus four Court Cards. Minor Arcana deal with everyday situations, practical matters, and the day-to-day aspects of life. They provide detail and context to readings.",
        category: "tarot",
        relatedTerms: ["Arcana", "Major Arcana", "Suits"]
    },
    {
        term: "Reversed Cards",
        definition: "Cards that appear upside-down in a reading. Interpretations vary by reader, but reversed cards often suggest blocked energy, delays, internal rather than external expression, or the shadow side of the card's meaning. Some readers don't use reversals while others consider them essential for nuanced readings.",
        category: "tarot",
        relatedTerms: ["Card Meanings", "Reading", "Interpretation"]
    },
    {
        term: "Spread",
        definition: "The specific layout or pattern in which tarot cards are placed during a reading. Each position in a spread has a designated meaning (past, present, future, obstacles, advice, etc.). Common spreads include the three-card spread, Celtic Cross, and single-card daily draws. The spread structure guides interpretation.",
        category: "tarot",
        relatedTerms: ["Reading", "Positions", "Layout"]
    },
    {
        term: "Suits",
        definition: "The four categories of Minor Arcana cards: Wands (Fire—passion, creativity, action), Cups (Water—emotions, relationships, intuition), Swords (Air—intellect, conflict, truth), and Pentacles (Earth—material matters, work, health). Each suit corresponds to an element and aspect of life, helping interpret the card's domain.",
        category: "tarot",
        relatedTerms: ["Minor Arcana", "Elements", "Wands", "Cups", "Swords", "Pentacles"]
    },

    // General Terms
    {
        term: "Age Calculator",
        definition: "A tool that computes your exact age from your date of birth to the current moment. Unlike simple 'years old' counts, precise age calculators break down age into multiple units: years, months, weeks, days, hours, minutes, and seconds. They account for leap years and varying month lengths to ensure mathematical accuracy.",
        category: "general",
        relatedTerms: ["Birthday", "Leap Year", "Time"]
    },
    {
        term: "Compatibility",
        definition: "In astrology, the analysis of how well two people's charts interact based on their zodiac signs, planetary aspects, and other factors. Simple Sun sign compatibility is popular but limited; deeper compatibility analysis (synastry) compares full birth charts. Both Western and Chinese astrology have traditional compatibility guidelines.",
        category: "general",
        relatedTerms: ["Synastry", "Sun Sign", "Relationships"]
    },
    {
        term: "Divination",
        definition: "The practice of seeking knowledge of the future or unknown through supernatural means or symbolic interpretation. Astrology, tarot, I Ching, and numerology are forms of divination. Modern practitioners often view divination as a tool for self-reflection rather than literal fortune-telling.",
        category: "general",
        relatedTerms: ["Tarot", "Astrology", "Symbolism"]
    },
    {
        term: "Horoscope",
        definition: "An astrological forecast based on the positions of celestial bodies. Daily, weekly, or monthly horoscopes published in media are based on Sun signs and offer general guidance. Personal horoscopes based on full birth charts provide more specific insights. The word comes from Greek words meaning 'time' and 'observer.'",
        category: "general",
        relatedTerms: ["Sun Sign", "Forecast", "Astrology"]
    },
    {
        term: "Leap Year",
        definition: "A year containing 366 days instead of 365, with February 29 as the extra day. Leap years occur every four years (with century exceptions) to keep the calendar aligned with Earth's orbit. Age calculators must account for leap years to provide accurate day counts, especially for people born on February 29.",
        category: "general",
        relatedTerms: ["Calendar", "Age Calculator", "February 29"]
    },
    {
        term: "Life Expectancy",
        definition: "The statistical average number of years a person is expected to live based on current mortality rates. Global average is approximately 73 years, varying by country, gender, and lifestyle factors. Life progress calculators use this figure to visualize what percentage of expected lifespan has passed.",
        category: "general",
        relatedTerms: ["Life Progress", "Mortality", "Statistics"]
    },
    {
        term: "Memento Mori",
        definition: "Latin for 'remember that you will die'—a philosophical concept encouraging awareness of mortality to live more intentionally. Life progress visualizations are modern forms of memento mori, not meant to cause anxiety but to inspire appreciation of time and purposeful living.",
        category: "general",
        relatedTerms: ["Life Progress", "Philosophy", "Mindfulness"]
    },
    {
        term: "Mysticism",
        definition: "The belief in or pursuit of union with the divine or spiritual truths through direct experience or intuition rather than logical reasoning. Astrological and tarot practices often incorporate mystical worldviews, viewing symbols as gateways to deeper understanding of self and cosmos.",
        category: "general",
        relatedTerms: ["Spirituality", "Symbolism", "Wisdom"]
    },
    {
        term: "Numerology",
        definition: "The study of the mystical significance of numbers and their influence on human life. In numerology, birth dates, names, and other numbers are reduced to single digits (or master numbers) to reveal personality traits, life paths, and timing. It often complements astrological practice.",
        category: "general",
        relatedTerms: ["Numbers", "Life Path", "Birthday"]
    },
    {
        term: "Symbolism",
        definition: "The use of symbols to represent ideas, qualities, or concepts beyond their literal meaning. Astrological signs, tarot imagery, and zodiac animals are symbolic systems encoding psychological and cultural meanings. Understanding symbolism helps interpret these tools on multiple levels.",
        category: "general",
        relatedTerms: ["Archetypes", "Interpretation", "Meaning"]
    }
];

const CATEGORY_LABELS: Record<string, { label: string; icon: React.ReactNode; color: string }> = {
    western: { label: 'Western Astrology', icon: <Sun size={16} />, color: 'text-yellow-400' },
    chinese: { label: 'Chinese Zodiac', icon: <Moon size={16} />, color: 'text-red-400' },
    tarot: { label: 'Tarot', icon: <Sparkles size={16} />, color: 'text-purple-400' },
    elements: { label: 'Elements', icon: <Star size={16} />, color: 'text-green-400' },
    general: { label: 'General', icon: <BookOpen size={16} />, color: 'text-blue-400' }
};

const GlossaryPage: React.FC = () => {
  // Set SEO meta tags for this page
  useSetMeta({
    title: 'Astrology & Tarot Glossary - Complete Terms & Definitions | ZodiAge',
    description: 'Comprehensive glossary of astrology, zodiac, tarot, and mystical terms. Learn definitions of Western astrology, Chinese zodiac, tarot cards, and spiritual concepts.',
    ogImage: 'https://zodiage.com/logo.png'
  });
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCategory, setSelectedCategory] = useState<string>('all');
    const [expandedTerm, setExpandedTerm] = useState<string | null>(null);

    const filteredTerms = useMemo(() => {
        return GLOSSARY_TERMS.filter(term => {
            const matchesSearch = term.term.toLowerCase().includes(searchQuery.toLowerCase()) ||
                                  term.definition.toLowerCase().includes(searchQuery.toLowerCase());
            const matchesCategory = selectedCategory === 'all' || term.category === selectedCategory;
            return matchesSearch && matchesCategory;
        }).sort((a, b) => a.term.localeCompare(b.term));
    }, [searchQuery, selectedCategory]);

    const categoryCounts = useMemo(() => {
        const counts: Record<string, number> = { all: GLOSSARY_TERMS.length };
        GLOSSARY_TERMS.forEach(term => {
            counts[term.category] = (counts[term.category] || 0) + 1;
        });
        return counts;
    }, []);

    return (
        <div className="space-y-12 animate-in fade-in duration-500 pb-12">
            {/* Header with Educational Content */}
            <article className="max-w-4xl mx-auto">
                <header className="text-center mb-8">
                    <h1 className="text-4xl md:text-5xl font-display font-bold text-white mb-4">
                        Astrology & Tarot Glossary – Complete Reference Guide
                    </h1>
                    <p className="text-gray-300 text-lg max-w-2xl mx-auto leading-relaxed">
                        A comprehensive dictionary of terms from Western astrology, Chinese zodiac, tarot, and mystical traditions. 
                        Learn the language of the stars and deepen your understanding of symbolic systems.
                    </p>
                </header>

                {/* Educational Introduction */}
                <section className="bg-cosmic-800/30 p-6 rounded-2xl border border-cosmic-700/50 mb-8">
                    <h2 className="text-xl font-bold text-white mb-3">Understanding Astrological Language</h2>
                    <p className="text-gray-300 text-sm leading-relaxed mb-3">
                        Astrology, tarot, and related practices have developed over thousands of years, creating a rich vocabulary 
                        of specialized terms. This glossary serves as an educational resource to help you understand the concepts 
                        underlying these symbolic systems.
                    </p>
                    <p className="text-gray-300 text-sm leading-relaxed mb-3">
                        Whether you're exploring your birth chart for the first time, learning to read tarot cards, or curious about 
                        Chinese zodiac traditions, knowing the terminology helps you engage more meaningfully with these practices. 
                        Each definition includes context, related terms, and practical explanations.
                    </p>
                    <p className="text-gray-300 text-sm leading-relaxed">
                        Remember that these systems are symbolic frameworks for self-reflection and cultural understanding, not 
                        scientific predictions. Use this knowledge to enrich your exploration of ZodiAge's tools and your own 
                        journey of self-discovery.
                    </p>
                </section>

                {/* How to Use This Glossary */}
                <section className="bg-cosmic-800/30 p-6 rounded-2xl border border-cosmic-700/50 mb-8">
                    <h2 className="text-xl font-bold text-white mb-3">How to Use This Glossary</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-300">
                        <div className="bg-black/20 p-4 rounded-xl">
                            <div className="text-primary font-bold mb-2">Search by Keyword</div>
                            <p>Type any term or concept in the search bar. The glossary will filter to show matching entries in real-time.</p>
                        </div>
                        <div className="bg-black/20 p-4 rounded-xl">
                            <div className="text-primary font-bold mb-2">Filter by Category</div>
                            <p>Use the category tabs to focus on Western astrology, Chinese zodiac, tarot, elements, or general terms.</p>
                        </div>
                        <div className="bg-black/20 p-4 rounded-xl">
                            <div className="text-primary font-bold mb-2">Explore Related Terms</div>
                            <p>Each definition includes links to related concepts, helping you build a comprehensive understanding.</p>
                        </div>
                    </div>
                </section>
            </article>

            {/* Search and Filter */}
            <div className="max-w-4xl mx-auto space-y-4">
                <div className="relative">
                    <Search size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                        type="text"
                        placeholder="Search terms, definitions, or concepts..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full bg-cosmic-800/50 border border-cosmic-700/50 rounded-xl pl-12 pr-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-primary transition-colors"
                    />
                </div>

                {/* Category Filters */}
                <div className="flex flex-wrap gap-2">
                    <button
                        onClick={() => setSelectedCategory('all')}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                            selectedCategory === 'all' 
                                ? 'bg-primary text-black' 
                                : 'bg-cosmic-800/50 text-gray-300 hover:bg-cosmic-700/50'
                        }`}
                    >
                        All ({categoryCounts.all})
                    </button>
                    {Object.entries(CATEGORY_LABELS).map(([key, { label, icon, color }]) => (
                        <button
                            key={key}
                            onClick={() => setSelectedCategory(key)}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                                selectedCategory === key 
                                    ? 'bg-primary text-black' 
                                    : `bg-cosmic-800/50 ${color} hover:bg-cosmic-700/50`
                            }`}
                        >
                            {icon}
                            {label} ({categoryCounts[key] || 0})
                        </button>
                    ))}
            </div>
        </div>

        <div className="max-w-4xl mx-auto space-y-6">
            {filteredTerms.length === 0 ?
                <div className="text-center py-12 text-gray-400">
                    <BookOpen size={48} className="mx-auto mb-4 opacity-50" />
                    <p>No terms found matching your search.</p>
                    <p className="text-sm mt-2">Try a different keyword or clear your filters.</p>
                </div>
            :
                    filteredTerms.map((term, index) => {
                        const category = CATEGORY_LABELS[term.category];
                        const isExpanded = expandedTerm === term.term;
                        
                        return (
                            <React.Fragment key={term.term}>
                                <div
                                    className={`bg-cosmic-800/30 border border-cosmic-700/50 rounded-xl overflow-hidden transition-all ${
                                        isExpanded ? 'shadow-lg shadow-primary/10' : ''
                                    }`}
                                >
                                    <button
                                        onClick={() => setExpandedTerm(isExpanded ? null : term.term)}
                                        className="w-full text-left p-4 flex items-start justify-between gap-4 hover:bg-cosmic-700/20 transition-colors"
                                    >
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2 mb-1">
                                                <span className={`${category.color} flex items-center gap-1 text-xs`}>
                                                    {category.icon}
                                                    {category.label}
                                                </span>
                                            </div>
                                            <h3 className="text-lg font-bold text-white">{term.term}</h3>
                                            {!isExpanded && (
                                                <p className="text-gray-400 text-sm mt-1 line-clamp-2">{term.definition}</p>
                                            )}
                                        </div>
                                        <div className={`text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}>
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M6 9l6 6 6-6" />
                                            </svg>
                                        </div>
                                    </button>
                                    
                                    {isExpanded && (
                                        <div className="px-4 pb-4 animate-in fade-in slide-in-from-top-2">
                                            <p className="text-gray-300 leading-relaxed mb-4">{term.definition}</p>
                                            {term.relatedTerms && term.relatedTerms.length > 0 && (
                                                <div className="flex flex-wrap gap-2">
                                                    <span className="text-gray-500 text-sm">Related:</span>
                                                    {term.relatedTerms.map((related) => (
                                                        <button
                                                            key={related}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setSearchQuery(related);
                                                                setSelectedCategory('all');
                                                            }}
                                                            className="text-primary text-sm hover:underline"
                                                        >
                                                            {related}
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                                {(index === 9 || index === 19) && (
                                    <AdSpace type="in-feed" slot="7700056531" layoutKey="-fb+5w+4e-db+86" className="my-4" />
                                )}
                            </React.Fragment>
                        );
                    })
                }
            </div>

            {/* Educational Footer */}
            <article className="max-w-4xl mx-auto space-y-8 mt-12">
                <section className="bg-cosmic-800/30 p-6 rounded-2xl border border-cosmic-700/50">
                    <h2 className="text-xl font-bold text-white mb-3">Expanding Your Knowledge</h2>
                    <p className="text-gray-300 text-sm leading-relaxed mb-3">
                        This glossary provides foundational knowledge for understanding astrological and mystical concepts. 
                        The definitions are written to be accessible to beginners while providing enough depth for those 
                        seeking to deepen their understanding.
                    </p>
                    <p className="text-gray-300 text-sm leading-relaxed">
                        As you explore ZodiAge's tools—from the age calculator to zodiac compatibility to tarot readings—refer 
                        back to this glossary to understand the terminology used. Each term is a gateway to deeper exploration 
                        of these ancient symbolic systems that continue to offer frameworks for self-reflection and cultural understanding.
                    </p>
                </section>

                <section className="bg-cosmic-800/30 p-6 rounded-2xl border border-cosmic-700/50">
                    <h2 className="text-xl font-bold text-white mb-3">Important Disclaimer</h2>
                    <p className="text-gray-300 text-sm leading-relaxed">
                        The information in this glossary is provided for educational and entertainment purposes only. 
                        Astrology, tarot, and related practices are symbolic systems with cultural and historical significance, 
                        not scientifically validated methods for prediction or diagnosis. Use this knowledge for personal 
                        reflection and cultural appreciation, and always consult qualified professionals for important life decisions.
                    </p>
                </section>

                {/* Author Bio - E-E-A-T Signal */}
                <AuthorBio variant="compact" topic="astrological terminology and symbolic systems" />
            </article>

            {/* Back to Top Button */}
            <div className="flex justify-center mt-8">
                <button
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                    className="flex items-center gap-2 bg-gradient-to-r from-purple-600 via-purple-500 to-pink-500 text-white px-6 py-3 rounded-full shadow-lg shadow-purple-600/30 transition-all hover:scale-105 hover:shadow-purple-600/50"
                >
                    <ArrowUp size={18} />
                    <span>Back to Top</span>
                </button>
            </div>
        </div>
    );
};

export default GlossaryPage;
